import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { createPortal } from 'react-dom';
import { TbClockHour10 } from "react-icons/tb";
import { RiDeleteBin4Fill } from 'react-icons/ri';
import TimeKeeper from 'react-timekeeper';
import { toast } from 'react-toastify';

interface CheckInsProps {
    reminders: Array<{
        id: string;
        text: string;
        time: string;
        completed: boolean;
    }>;
    setReminders: React.Dispatch<React.SetStateAction<Array<{
        id: string;
        text: string;
        time: string;
        completed: boolean;
    }>>>;
}

// Helper to format time to 12-hour format
const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
};

const CheckIns: React.FC<CheckInsProps> = ({
    reminders,
    setReminders,
}) => {
    const [newReminder, setNewReminder] = useState('');
    const [reminderTime, setReminderTime] = useState('');
    const [showTimePicker, setShowTimePicker] = useState(false);

    // Sound alert function
    const playAlertSound = () => {
        try {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            const audioContext = new AudioContextClass();

            // Resume audio context if suspended
            if (audioContext.state === 'suspended') {
                audioContext.resume();
            }

            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = 880; // A5
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (error) {
            console.error('Error playing alert sound:', error);
        }
    };

    // Check for reminder alerts
    useEffect(() => {
        const checkReminders = () => {
            const now = new Date();
            const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');

            reminders.forEach(reminder => {
                if (reminder.time === currentTime && !reminder.completed) {
                    // Play sound
                    playAlertSound();

                    // Show toast
                    toast.info(
                        <div className="flex items-center gap-4 min-w-[200px]">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30 ring-2 ring-white/20">
                                <TbClockHour10 className="text-white text-xl animate-pulse" />
                            </div>
                            <div className="flex flex-col flex-1">
                                <span className="font-bold text-gray-900 leading-tight">Your reminder at {formatTime(reminder.time)}</span>
                                <span className="text-sm text-gray-600 leading-snug mt-0.5">{reminder.text}</span>
                            </div>
                        </div>,
                        {
                            icon: false,
                            autoClose: false,
                            position: "top-right",
                            containerId: "session-toast",
                            className: "!bg-white/90 !backdrop-blur-xl !border !border-orange-500/30 !rounded-2xl !shadow-2xl !p-4 !font-sans",
                            progressClassName: "!bg-gradient-to-r !from-orange-400 !to-orange-600",
                        }
                    );

                    // Mark as completed
                    setReminders(prev => prev.map(r =>
                        r.id === reminder.id ? { ...r, completed: true } : r
                    ));
                }
            });
        };

        const interval = setInterval(checkReminders, 1000); // Check every second
        return () => clearInterval(interval);
    }, [reminders]);

    const onAddReminder = () => {
        if (newReminder.trim() && reminderTime) {
            const reminder = {
                id: Date.now().toString(),
                text: newReminder.trim(),
                time: reminderTime,
                completed: false
            };
            setReminders(prev => [...prev, reminder]);
            setNewReminder('');
            setReminderTime('');
        }
    };

    const onToggleReminder = (reminderId: string) => {
        setReminders(prev => prev.map(reminder =>
            reminder.id === reminderId ? { ...reminder, completed: !reminder.completed } : reminder
        ));
    };

    const onDeleteReminder = (reminderId: string) => {
        setReminders(prev => prev.filter(reminder => reminder.id !== reminderId));
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                    Check-ins
                </h3>
            </div>

            {/* Add Reminder - Simplified */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <input
                    type="text"
                    value={newReminder}
                    onChange={(e) => setNewReminder(e.target.value)}
                    placeholder="Gentle check-in text..."
                    className="flex-1 bg-white text-gray-900 rounded-lg p-3 border border-gray-200/60 focus:border-orange-300 focus:ring-orange-200 focus:ring-opacity-50 focus:outline-none text-sm placeholder:text-gray-400"
                />

                {/* Custom Time Picker */}
                <div className="relative w-full sm:w-32">
                    <button
                        onClick={() => setShowTimePicker(true)}
                        className="w-full flex items-center justify-between bg-white text-gray-900 rounded-lg p-3 border border-gray-200/60 focus:border-orange-300 focus:outline-none text-sm h-[46px]"
                    >
                        <span className={!reminderTime ? "text-gray-400" : ""}>
                            {reminderTime ? moment(reminderTime, 'HH:mm').format('h:mm A') : "Select time"}
                        </span>
                        <TbClockHour10 className="text-lg" color='rgba(255, 89, 36, 0.9)' />
                    </button>

                    {showTimePicker && createPortal(
                        <div
                            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4"
                            onClick={() => setShowTimePicker(false)}
                        >
                            <div
                                className="shadow-2xl rounded-2xl overflow-hidden bg-white animate-in fade-in zoom-in duration-200"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <TimeKeeper
                                    time={reminderTime || '12:00'}
                                    onChange={(data) => setReminderTime(data.formatted24)}
                                    onDoneClick={() => setShowTimePicker(false)}
                                    switchToMinuteOnHourSelect
                                    closeOnMinuteSelect={false}
                                    hour24Mode={false}
                                    coarseMinutes={1}
                                    forceCoarseMinutes
                                />
                            </div>
                        </div>,
                        document.body
                    )}
                </div>
                <button
                    onClick={onAddReminder}
                    className="px-6 py-3 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-lg font-medium transition-colors text-sm"
                >
                    Add check ins
                </button>
            </div>

            {/* Reminder List - Simplified */}
            <div className="space-y-3 max-h-64 sm:max-h-80 overflow-y-auto">
                {[...reminders].sort((a, b) => {
                    // Sort by completion status (uncompleted first)
                    if (a.completed === b.completed) return 0;
                    return a.completed ? 1 : -1;
                }).map((reminder) => (
                    <div key={reminder.id} className="flex items-center space-x-3 p-4 bg-white border border-gray-200/60 rounded-xl hover:border-orange-200/60 hover:shadow-sm transition-all">
                        <button
                            onClick={() => onToggleReminder(reminder.id)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 text-xs ${reminder.completed
                                ? 'bg-orange-600 border-orange-600 text-white'
                                : 'border-gray-300 hover:border-orange-400'
                                }`}
                        >
                            {reminder.completed && '✓'}
                        </button>
                        <div className="flex-1 min-w-0">
                            <div className={`text-sm ${reminder.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                {reminder.text}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{moment(reminder.time, 'HH:mm').format('h:mm A')}</div>
                        </div>
                        <button
                            onClick={() => onDeleteReminder(reminder.id)}
                            className="text-gray-400 hover:text-orange-600 text-sm p-1 flex-shrink-0 transition-colors"
                            title="Delete check-in"
                        >
                            <RiDeleteBin4Fill className="text-base" />
                        </button>
                    </div>
                ))}
                {reminders.length === 0 && (
                    <div className="text-gray-500 text-center py-12 text-sm">No check-ins yet.</div>
                )}
            </div>
        </div>
    );
};

export default CheckIns;
